/**
 * Design Tokens System
 *
 * Centralized design tokens for the Switch Energy brand.
 * This system provides a consistent set of design primitives for:
 * - Spacing and layout
 * - Typography hierarchy
 * - Color palette with WCAG AA compliance
 * - Border radius and widths
 * - Shadows and elevation
 *
 * Usage:
 * ```typescript
 * import { tokens } from 'src/design-tokens';
 *
 * // In a component
 * <Box sx={{
 *   padding: tokens.spacing.md,
 *   borderRadius: tokens.borderRadius.lg,
 *   color: tokens.colors.text.primary,
 *   fontSize: tokens.typography.fontSize.md,
 *   boxShadow: tokens.shadows.card,
 * }}>
 * ```
 *
 * @module design-tokens
 */

import * as borderTokens from './borders';
import * as colorTokens from './colors';
import * as shadowTokens from './shadows';
import * as spacingTokens from './spacing';
import * as typographyTokens from './typography';

/**
 * Main design tokens object
 * Contains all design primitives organized by category
 */
export const tokens = {
  // Spacing tokens
  spacing: spacingTokens.spacing,
  semanticSpacing: spacingTokens.semanticSpacing,

  // Border tokens
  borderRadius: borderTokens.borderRadius,
  borderWidth: borderTokens.borderWidth,
  semanticBorders: borderTokens.semanticBorders,

  // Typography tokens
  typography: typographyTokens.typography,
  semanticTypography: typographyTokens.semanticTypography,

  // Color tokens (shadcn-style semantic colors)
  colors: colorTokens.colors,

  // Extended color palettes
  extendedColors: colorTokens.extendedColors,

  // Brand colors
  brandColors: colorTokens.brandColors,

  // Alpha colors
  alpha: colorTokens.alpha,

  // Shadow tokens
  shadows: shadowTokens.shadows,
  coloredShadows: shadowTokens.coloredShadows,
  innerShadows: shadowTokens.innerShadows,
  semanticShadows: shadowTokens.semanticShadows,
  focusRings: shadowTokens.focusRings,
} as const;

/**
 * Re-export individual token modules for direct access
 */
export {
  borderTokens as borders,
  colorTokens as colors,
  shadowTokens as shadows,
  spacingTokens as spacing,
  typographyTokens as typography,
};

/**
 * Export all types
 */
export type {
  BorderRadiusKey,
  BorderWidthKey,
  SemanticBordersKey,
} from './borders';

export type {
  BrandColorKey,
  ColorKey,
  ExtendedBackgroundKey,
  ExtendedBorderKey,
  ExtendedTextKey,
} from './colors';

export type {
  ColoredShadowKey,
  FocusRingKey,
  InnerShadowKey,
  SemanticShadowKey,
  ShadowKey,
} from './shadows';

export type { SemanticSpacingKey, SpacingKey } from './spacing';

export type {
  FontFamilyKey,
  FontSizeKey,
  FontWeightKey,
  LetterSpacingKey,
  LineHeightKey,
  SemanticTypographyKey,
} from './typography';

/**
 * Default export for convenient importing
 */
export default tokens;
